import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppColors, config, typographyClamps } from '../utils/utils';
import useToggle from '../hooks/useToggle';
import Page from '../components/Page';
// import GetStartedModel from '../components/models/GetStartedModel';
import VideoModel from '../components/models/VideoModel';
import { useNavigate } from 'react-router-dom';
import { HomeSectionView } from '../components/HomeSectionView';
import BookDemoFormModel from '../components/models/BookDemoFormModel';
import ImagePreviewDialog from '../components/models/ImagePreviewDialog';
import AudioModel from '../components/models/AudioModel';
import NfadaPopupModal from '../components/models/NfadaPopupModal';
import PricingModal from '../components/models/PricingModal';
import { getItem } from '../utils/localStorage';
import { IsNotNullOrEmpty } from '../utils/valid';
import QRBuddySubscriptionDialog from '../components/models/QRBuddySubscriptionDialog';
import { pricingPlans } from './Pricing';

interface GradientBorderBoxProps {
  children: React.ReactNode;
  gradient: string;
  borderWidth?: number;
  borderRadius?: number;
  padding?: number;
}

const GradientBorderBox: React.FC<GradientBorderBoxProps> = ({
  children,
  gradient,
  borderWidth = 3,
  borderRadius = 24,
  padding = 1,
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        padding: `${borderWidth}px`,
        background: gradient,
        borderRadius: `${borderRadius}px`,
        // marginLeft: 'auto',
        width: 'fit-content',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: `${borderRadius - borderWidth}px`,
          padding: padding,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface Feature {
  iconPath: string;
  title?: string;
  description?: string;
  subtitle1?: string;
  subtitle2?: string;
  imageOnLeft?: boolean;
  videoUrl?: string;
  audioUrl?: string;
  link?: string;
}

const mainFeatureCars: Feature[] = [
  {
    title: 'The Lot',
    iconPath: '/static/home_page/img_the_lot.png',
    link: '/the-lot',
  },
  {
    title: 'The web',
    iconPath: '/static/home_page/img_the_web.png',
    link: '/the-web',
  },
  {
    title: 'Dashboards',
    iconPath: '/static/home_page/img_dashboards.png',
    link: '/#',
  },
  {
    title: 'AI Pro Advisors ',
    iconPath: '/static/home_page/img_ai_pro_advisors.png',
    link: '/#',
  },
];

const audioCards: Feature[] = [
  {
    iconPath: '/static/home_page/img_audio_1.png',
    audioUrl: '/audio/What_Creates_a_Quality_Lead.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_2.png',
    audioUrl: '/audio/Why_Buyer_Behavior_Matters.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_3.png',
    audioUrl: '/audio/What_is_Ghost_Data.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_4.png',
    audioUrl: '/audio/Spotlight_vs_VDP.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_5.png',
    audioUrl: '/audio/What_Is_the_QRBuddy_Web_Raill.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_6.png',
    audioUrl: '/audio/Creating_Quality_Leads.wav',
  },
];

// const featureVideoCards: Feature[] = [
//   {
//     title: 'The First 60 Seconds That Decide the Sale',
//     description:
//       'The moment a shopper scans, clicks, or calls — the clock starts. This video reveals why real-time engagement in the first 60 seconds now determines who wins the sale, and how QR Buddy captures and routes buyers instantly across the lot, web, and phone.',
//     iconPath: '/static/home_page/video_thumb_1.png',
//     videoUrl: 'https://assets.qrbuddy.ai/web/First-60-Seconds.mp4',
//   },
//   {
//     title: 'The Problem with Modern Lead Systems',
//     description:
//       'Traditional lead systems are broken — selling names instead of building real buying experiences. In this video, Rob explains why speed, engagement, and first-moment connection now decide who wins the sale, and how QR Buddy rebuilds the front line of car sales for today’s buyer.',
//     iconPath: '/static/home_page/video_thumb_2.png',
//     videoUrl: 'https://assets.qrbuddy.ai/web/Problem-with-Lead-Systems.mp4',
//   },
//   {
//     title: 'High-Tech Meets High-Touch',
//     description:
//       'AI should empower people — not replace them. In this video, Rob explains how QR Buddy blends real-time AI engagement with human connection to protect your sales team, your reputation, and your long-term growth.',
//     iconPath: '/static/home_page/video_thumb_3.png',
//     videoUrl: 'https://assets.qrbuddy.ai/web/High-Tech-High-Touch.mp4',
//   },
// ];

const Home: React.FC = () => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  // const xMargin = { xs: 0, sm: 0, md: '8%', lg: '10%' };

  // const headerActionButtonImageSize = {
  //   xs: '100% auto',
  //   sm: '100% auto',
  //   md: '100% auto',
  //   lg: '100% 100%',
  // };

  const navigate = useNavigate();

  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  const [audioUrl, setAudioUrl] = useState<string | undefined>();
  const [isIntroVideoPlaying, setIsIntroVideoPlaying] = useState<boolean>(false);
  const [isGetStartedShow, setIsGetStartedShow] = useState<boolean>(false);
  // const [isPlayIntroVideo] = useState<boolean>(false);

  // const [expanded, setExpanded] = useState<string | false>(false);

  const scrollRefHowItWorks = useRef<HTMLDivElement>(null);

  // const handleChange =
  //   (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
  //     setExpanded(isExpanded ? panel : false);
  //   };

  const { toggle, onOpen, onClose } = useToggle();
  const { toggle: getStartedToggle, onOpen: getStartedOnOpen, onClose: getStartedOnClose } = useToggle();
  const {
    toggle: audioToggle,
    onOpen: audioOnOpen,
    onClose: audioOnClose,
  } = useToggle();

  const {
    toggle: toggleRequestDemoModel,
    onOpen: onOpenRequestDemoModel,
    onClose: onCloseRequestDemoModel,
  } = useToggle();

  const {
    toggle: toggleGetStartedModel,
    onOpen: onOpenGetStartedModel,
    onClose: onCloseGetStartedModel,
  } = useToggle();


  const {
    toggle: togglePricingModel,
    // onOpen: onOpenPricingModel,
    onClose: onClosePricingModel,
  } = useToggle();

  const [previewImagePath, setPreviewImagePath] = useState<
    string | undefined
  >();

  const {
    toggle: toggleImagePreviewModel,
    onOpen: onOpenImagePreviewModel,
    onClose: onCloseImagePreviewModel,
  } = useToggle();

  const openImageViewView = (imagePath: string | undefined) => {
    if (imagePath !== undefined) {
      setPreviewImagePath(imagePath);
      onOpenImagePreviewModel();
    }
  };

  useEffect(() => {
    if (!isGetStartedShow) {
      getStartedOnOpen();
      setIsGetStartedShow(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (toggle === false) {
      setVideoUrl(undefined);
    }
  }, [toggle]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePlayVideo = (videoUrl: string | undefined) => {
    if (videoUrl !== undefined) {
      setVideoUrl(videoUrl);
      onOpen();
    }
  };

  useEffect(() => {
    if (audioToggle === false) {
      setAudioUrl(undefined);
    }
  }, [audioToggle]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePlayAudio = (audioUrl: string | undefined) => {
    if (audioUrl !== undefined) {
      setAudioUrl(audioUrl);
      audioOnOpen();
    }
  };


  const handlePayNow = (planCode: string) => {
    let webUrl = `${config.WEB_URL}/plan-terms?plan=${planCode}`;

    const storedReferralCode = getItem(config.REFERRAL_CODE_PARAM_KEY);
    if (IsNotNullOrEmpty(storedReferralCode)) {
      webUrl += `&${config.REFERRAL_CODE_PARAM_KEY}=${storedReferralCode}`;
    }

    window.open(`${webUrl}`) || window.location.replace(`${webUrl}`);
  };


  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCallToAssistant = () => {
    let telLink = `tel:${config.AUTO_ASSISTANT_CONTACT_NUMBER}`;
    window.open(`${telLink}`, '_blank') ||
      window.location.replace(`${telLink}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const scrollToHowItWorks = () => {
    const el = scrollRefHowItWorks.current;
    if (!el) return;

    // el?.scrollIntoView({ behavior: 'smooth' });

    const yOffset = -50; // scroll 50px less (adjust as needed)
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigateToHowItWorksPage = () => {
    navigate('/how-it-works');
  };

  const handleGetStartedClick = () => {
    onOpenGetStartedModel();
    // navigate('/pricing');
  };

  // const navigateToPage = (page: string) => {
  //   navigate(page);
  // };

  const getBookADemoButton = (setMarginTop = true) => {
    return (
      <Button
        variant='contained'
        sx={{
          mt: setMarginTop ? { xs: 1, sm: 2 } : 'auto',
          width: 'fit-content',
          minWidth: '150px',
          height: 42,
          border: '1px solid white',
          borderRadius: '50px',
          // border: 0,
          color: 'white',
          padding: '10px 24px',
          backgroundColor: AppColors.vividBlueColor2,
          // fontWeight: 'bold',
          textTransform: 'none',
          fontSize: 'clamp(0.8rem, 2vw, 1.3rem)',
          lineHeight: 1.5,
        }}
        onClick={onOpenRequestDemoModel}
      >
        Book a Demo
      </Button>
    );
  };

  // const getGetStartedButton2 = () => {
  //   return (
  //     <GradientBorderBox
  //       gradient={AppColors.aiGradient}
  //       borderWidth={2}
  //       borderRadius={50}
  //       padding={0}
  //     >
  //       <Button
  //         onClick={() => {
  //           handleGetStartedClick();
  //         }}
  //         sx={{
  //           // mt: { xs: 1, sm: 2 },
  //           width: 'fit-content',
  //           minWidth: '150px',
  //           height: 42,
  //           borderRadius: '48px',
  //           padding: '10px 24px',
  //           backgroundColor: 'white',
  //           color: 'black',
  //           // fontWeight: 'bold',
  //           textTransform: 'none',
  //           fontSize: 'clamp(0.8rem, 2vw, 1.3rem)',
  //           lineHeight: 1.5,
  //           boxShadow: 'none',
  //         }}
  //       >
  //         Get Started
  //       </Button>
  //     </GradientBorderBox>
  //   );
  // };

  const getGetStartedButton = () => {
    const borderWidth = 2;
    const targetHeight = 42; // match Book a Demo height

    return (
      <GradientBorderBox
        gradient={AppColors.aiGradient}
        borderWidth={borderWidth}
        borderRadius={50}
        padding={0}
      >
        <Button
          onClick={() => {
            handleGetStartedClick();
          }}
          sx={{
            width: 'fit-content',
            minWidth: `calc(150px - ${borderWidth * 2}px)`, // account for left+right border
            height: targetHeight - borderWidth * 2,         // account for top+bottom border → 38px
            borderRadius: '48px',
            padding: '10px 24px',
            backgroundColor: 'white',
            color: 'black',
            textTransform: 'none',
            fontSize: 'clamp(0.8rem, 2vw, 1.3rem)',
            lineHeight: 1.5,
            boxShadow: 'none',
          }}
        >
          Get Started
        </Button>
      </GradientBorderBox>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getLearnMoreAndGetStartedButtons = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 5,
          px: 3,
          position: 'relative',
        }}
      >
        <Button
          variant='contained'
          sx={{
            borderRadius: 0,
            bgcolor: AppColors.orangeBGColor,
            color: 'white',
            textTransform: 'none',
            padding: { xs: '4px 12px', sm: '8px 24px' },
            fontSize: typographyClamps.h6,
            whiteSpace: 'pre-line',
            lineHeight: 1.35,
            // my: { xs: 2, sm: 4 },
            my: 1,
            '&:hover': {
              borderRadius: 0,
            },
          }}
          onClick={() => {
            // onOpenRequestDemoModel();
          }}
        >
          Learn More
        </Button>
        <Button
          variant='outlined'
          sx={{
            borderRadius: 0,
            borderColor: AppColors.vividBlueColor2,
            borderWidth: 2,
            color: AppColors.vividBlueColor2,
            backgroundColor: 'white',
            textTransform: 'none',
            padding: { xs: '4px 12px', sm: '8px 24px' },
            fontSize: typographyClamps.h6,
            whiteSpace: 'pre-line',
            lineHeight: 1.35,
            my: 1,
            '&:hover': {
              borderColor: AppColors.vividBlueColor2,
              backgroundColor: 'rgba(0, 0, 0, 0.04)', // optional subtle hover
              borderRadius: 0,
            },
          }}
          onClick={() => {
            handleGetStartedClick();
          }}
        >
          Get Started
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Page title={'Home'}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // backgroundImage: `url('/static/home_page/home_banner_background .png')`,
            // backgroundPosition: 'center',
            // backgroundSize: 'cover',
            // backgroundRepeat: 'no-repeat',
            background: AppColors.bannerGradient,
            p: { xs: 2, sm: 4 },
            gap: 1,
            color: 'white',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
              flexDirection: {xs: 'column', sm: 'row'},
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 400,
                height: 200,
                overflow: 'hidden',
                position: 'absolute',
                left: -30,
                top: 0,
                display: { xs: 'none', md: 'block' },
              }}
            >
              <Box
                component='img'
                src='/static/home_page/img_georgraphy_pattern.png'
                sx={{
                  width: '200%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'left', // ensure left side is visible
                }}
              />
            </Box>

            <Box
              component={'img'}
              src='/static/images/one_line_logo_2.png'
              sx={{ maxHeight: { xs: '50px', sm: '70px', zIndex: 1,} }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                // marginLeft: { xs: undefined, sm: 'auto' },
              }}
            >
              {getBookADemoButton(false)}

              {getGetStartedButton()}
            </Box>
          </Box>

          <Box
            component={'img'}
            src='/static/images/ic_ai_star.png'
            sx={{ height: { xs: '50px', ms: '130px' }, mt: 1 }}
          />

          <Typography
            sx={{
              // fontSize: typographyClamps.fontSize18,
              fontSize: 'clamp(1.4rem, 5vw, 2.5rem)',
            }}
          >
            Introducing
          </Typography>

          <Typography
            sx={{
              fontSize: 'clamp(1.4rem, 5vw, 2.5rem)',
              fontWeight: 'bold',
              textAlign: 'center',
              whiteSpace: 'pre-line', // This allows text to break at newline characters
              lineHeight: 1.1,
              background: AppColors.aiGradient9,
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Our
            <Typography
              component='span'
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: 'white',
              }}
            >
              {' '}
              New{' '}
            </Typography>
            Technology Partner
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: typographyClamps.subtitle1,
                sm: typographyClamps.h4,
              },
              textAlign: 'center',
              pt: 2,
              whiteSpace: 'pre-line', // This allows text to break at newline characters
            }}
          >
            Where High-Tech Intelligence Meets High-Touch Engagement
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 'fit-content',
                maxWidth: '100%',
                cursor: isIntroVideoPlaying ? 'default' : 'pointer',
                overflow: 'hidden',
                lineHeight: 0,
              }}
              onClick={
                isIntroVideoPlaying ? undefined : () => setIsIntroVideoPlaying(true)
              }
            >
              <Box
                component='img'
                src='/static/home_page/img_home_video.png'
                sx={{ width: 'auto', maxWidth: '100%', height: 'auto', display: 'block' }}
              />
              {!isIntroVideoPlaying && (
                <Box
                  component='img'
                  src='/static/images/ic_play_orange.png'
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: { xs: 50, sm: 90 },
                    pointerEvents: 'none',
                  }}
                />
              )}
              {isIntroVideoPlaying && (
                <Box
                  component='video'
                  controls
                  autoPlay
                  playsInline
                  preload='metadata'
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                >
                  <source src={config.HOME_PAGE_INTRO_VIDEO} type='video/mp4' />
                  Your browser does not support the video tag.
                </Box>
              )}
            </Box>
          </Box>

          {/* <Grid
            container
            sx={{
              width: '100%',
              border: `1px solid white`,
              borderRadius: '24px',
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              gap: { xs: 2, sm: 0 },
              mt: 2,
            }}
          >
            <Grid size={{ xs: 12, sm: 5 }}>              
              <Box
                component={'img'}
                src='/static/home_page/img_home_video.png'
                sx={{ maxWidth: '100%', cursor: 'pointer' }}
                onClick={() => handlePlayVideo(config.HOME_PAGE_INTRO_VIDEO)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3.5 }} px={'4%'}>
              <Typography
                sx={{ fontSize: typographyClamps.h5, textAlign: 'center' }}
              >
                A <strong>system</strong> built on today’s technology,
                capabilities and logic
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 3.5 }}>
              <Box
                component={'img'}
                src='/static/home_page/img_process_flow.png'
                sx={{ maxWidth: '100%' }}
              />
            </Grid>
          </Grid> */}

          <Grid
            container
            sx={{
              width: '100%',
              textAlign: 'center',
              justifyContent: 'center',
              mt: { xs: 2, sm: 4 },
              gap: 2,
            }}
          >
            {mainFeatureCars.map((item) => {
              return (
                <Grid
                  size={{ xs: 5.7, sm: 2.7, md: 2.8 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center', // horizontal center
                  }}
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      position: 'relative',
                    }}
                  >
                    <Box
                      component={'img'}
                      src='/static/home_page/img_light_blue_circle_1.png'
                      sx={{
                        position: 'absolute',
                        top: '-10%',
                        left: '50%',
                        transform: 'translateX(-50%) rotate(180deg)',
                        maxHeight: '200px',
                        maxWidth: '100%',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: typographyClamps.fontSize20,
                        position: 'relative',
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Box
                      component={'img'}
                      src={item.iconPath}
                      sx={{ maxWidth: '100%', position: 'relative' }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {getBookADemoButton()}

          <Grid
            container
            mt={{ xs: 2, sm: 4 }}
            sx={{
              width: '100%',
              alignItems: 'center',
            }}
            spacing={{ xs: 2, sm: 4 }}
          >
            <Grid size={{ xs: 12, sm: 7 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  component={'img'}
                  src='/static/home_page/img_workflow.png'
                  sx={{ maxWidth: { xs: '100%', sm: '100%' }, mb: 2 }}
                />
                <GradientBorderBox
                  gradient={AppColors.aiGradient9}
                  borderWidth={2}
                >
                  <Typography
                    sx={{
                      fontSize: typographyClamps.h6,
                      background: AppColors.aiGradient9,
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      px: 2,
                    }}
                  >
                    This Isn’t a Tool — It’s a Workflow
                  </Typography>
                </GradientBorderBox>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <Typography
                sx={{
                  fontSize: {
                    xs: typographyClamps.h5,
                    sm: typographyClamps.fontSize16,
                    md: typographyClamps.fontSize18,
                    lg: typographyClamps.fontSize20,
                  },
                  mb: 1.5,
                }}
              >
                Know Exactly When to Engage — and What to Say
              </Typography>
              <Stack>
                {[
                  'Turning Activity Into Measurable Performance',
                  'From Recruitment to Revenue — Connected',
                  'Capturing and reporting analytics from the first click',
                  'High-Tech Intelligence. High-Touch Engagement',
                ].map((item, index) => (
                  <Stack
                    key={`flow-feature-${index + 1}`}
                    direction='row'
                    alignItems='start'
                    py={isMobile ? 0.5 : 1}
                    pl={1}
                  >
                    <Box
                      component='img'
                      src='/static/images/ic_ai_star.png'
                      // height='32px'
                      // width='32px'
                      height={{ xs: '24px', sm: typographyClamps.fontSize24 }}
                      width={{ xs: '24px', sm: typographyClamps.fontSize24 }}
                    // pt={1}
                    />
                    {/* )} */}
                    <Box sx={{ pl: isMobile ? 1 : 2 }}>
                      <Typography
                        variant='body1'
                        sx={{
                          fontSize: {
                            xs: typographyClamps.h5,
                            sm: typographyClamps.fontSize15,
                            md: typographyClamps.fontSize16,
                            lg: typographyClamps.fontSize18,
                          },
                          fontWeight: 'bold',
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            // width: '100%',
            textAlign: 'center',
            px: { xs: '5%', sm: '10%', md: '25%' },
            mx: 0,
            mt: { xs: 6, sm: 10 },
            mb: 4,
          }}
        >
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#0069e429',
              borderRadius: { xs: 4, sm: 6 },
            }}
          >
            <Box textAlign={'center'} mr={4}>
              <Box
                component={'img'}
                src='/static/images/rob_profile.png'
                sx={{
                  maxWidth: { xs: '120%', sm: '150%', md: '100%' },
                }}
              />
            </Box>
            <Stack
              sx={{
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: typographyClamps.body1,
                    sm: typographyClamps.h5,
                  },
                  fontWeight: 400,
                }}
              >
                <Box
                  component={'img'}
                  src='/static/home_page/img_quotation_mark_end.png'
                  height={{ xs: '16px', sm: '24px' }}
                  sx={{
                    position: 'relative',
                    top: -10,
                    left: 0,
                    opacity: 0.45,
                  }}
                />
                &nbsp; “It starts with the experience. The experience creates
                the data. And the data creates the lead.”&nbsp;
                <Box
                  component={'img'}
                  src='/static/home_page/img_quotation_mark_start.png'
                  height={{ xs: '16px', sm: '24px' }}
                  sx={{ position: 'relative', bottom: 5, opacity: 0.45 }}
                />
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
            // m: { xs: 2, sm: 4 },
            // borderRadius: 4,
            bgcolor: '#0e1a3a',
            color: 'white',
          }}
        >
          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              // fontWeight: 'bold',
            }}
          >
            Short audio insights on buyer behavior, AutoSpotlight, and what
            truly creates a quality lead.
          </Typography>

          <Box
            sx={{
              position: 'relative',
              my: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: typographyClamps.h4,
              }}
            >
              Dealer Questions. Clear Answers.
            </Typography>

            <Box
              component={'img'}
              src='/static/home_page/img_orange_underline.png'
              sx={{
                display: { xs: 'none', sm: 'block' },
                width: { xs: '90px', sm: '200px' },
                position: 'absolute',
                left: 0,
                bottom: -10,
              }}
            />
          </Box>

          <Grid
            container
            gap={{ xs: 1, sm: 2 }}
            my={{ xs: 2, sm: 4 }}
            justifyContent={'center'}
          >
            {audioCards.map((item) => {
              return (
                <Grid
                  size={{ xs: 3.7, sm: 3.7, md: 1.8 }}
                  key={item.title}
                  display={'flex'}
                >
                  <Box
                    component={'img'}
                    src={item.iconPath}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      handlePlayAudio(item.audioUrl);
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>

          {getBookADemoButton()}
        </Box>

        <Box>
          <HomeSectionView
            idx={0}
            label='<strong>Scanning</strong> on your lot'
            labelImagePath={'/static/home_page/ic_scanning.png'}
            title=''
            subtitle=''
            features={[
              {
                title: 'Turn every vehicle into a live digital sales asset',
                description:
                  'A quick QR scan instantly identifies the vehicle and launches a guided mobile buying journey — capturing interest before the shopper ever walks away.',
              },
              {
                title: 'Replace static window stickers with live engagement',
                description:
                  'Shoppers see real pricing, availability, payments, and instant chat — right from the windshield.',
              },
              {
                title: 'Own every walk-up interaction',
                description:
                  'QR Buddy converts anonymous lot traffic into tracked, routed, qualified digital prospects — eliminating lost ups and skipped logins.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_scanning_on_your_lot.png'}
            openImageViewView={openImageViewView}
          />

          <HomeSectionView
            idx={1}
            label='Your <strong>Website</strong> Traffic '
            labelImagePath={'/static/home_page/ic_website_traffic.png'}
            title=''
            subtitle=''
            features={[
              {
                title: 'Capture buyers before they bounce',
                description:
                  'The NEW QR Buddy Web Rail engages website visitors the moment they arrive — guiding their experience, capturing real-time behavior, and turning anonymous traffic into identified prospects before momentum fades.',
              },
              {
                title: 'Track real buying intent — not just page views',
                description:
                  'Just like we capture engagement on the lot, the Web Rail records buyer actions on your website — pricing clicks, trade-in exploration, finance interest, vehicle comparisons, and return visits — revealing who’s truly in-market.',
              },
              {
                title: 'Build a living digital buyer profile',
                description:
                  'Every interaction feeds into a dynamic, evolving buyer profile — combining lot scans and website behavior — so your team remains informed, follows up smarter, and closes with clarity.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_website_traffic.png'}
            openImageViewView={openImageViewView}
            onOpenRequestDemoModel={onOpenRequestDemoModel}
          />

          <HomeSectionView
            idx={2}
            label='Auto Spotlight'
            labelImagePath={'/static/home_page/ic_calling_dealership.png'}
            title=''
            subtitle=''
            features={[
              {
                title: 'Every Scan Becomes Measurable Intent',
                description:
                  'Auto Spotlight turns anonymous traffic into identifiable buyer behavior.',
              },
              {
                title: 'Clarity Replaces Guesswork',
                description:
                  'Know exactly what each shopper engaged with — before the conversation begins.',
              },
              {
                title: 'Guided Actions Drive Momentum',
                description:
                  'From pricing to trade-in to finance, buyers always know what to do next.',
              },
              {
                title: 'Intent Turns Into Quality Leads',
                description:
                  'When behavior is visible, follow-up becomes smarter — and more effective.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_auto_spotlight_mobile.png'}
            openImageViewView={(_link: string) => {
              openImageViewView(
                '/static/lot_page/img_auto_spotlight_mobile_big.png',
              );
            }}
          />

          <HomeSectionView
            idx={3}
            label='Dealer Dashboard'
            labelImagePath={'/static/home_page/ic_dealer_dashboard.png'}
            title=''
            subtitle=''
            features={[
              {
                title: 'Understand Key Metrics ',
                description:
                  'View critical information including number of sessions and average amount of time shoppers spend viewing your content, filtered by desired timeframe.',
              },
              {
                title: 'Gauge Shoppers’ Interests',
                description:
                  'Recent Shopper Sessions provides a bird’s-eye view of shopper interaction to reveal exactly where buyer interest is highest.',
              },
              {
                title: 'Identify Lead Source',
                description:
                  'Compare QR scans against direct website visits to  identify which channels are driving your highest engagement.',
              },
              {
                title: 'High-Intent  Analytics',
                description:
                  'Monitor the exact percentage of shoppers performing high-value actions, allowing you to separate casual browsers from ready-to-buy leads.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_dealer_dashboard2.png'}
            openImageViewView={(_link: string) => {
              openImageViewView(_link);
            }}
            onOpenRequestDemoModel={onOpenRequestDemoModel}
          />

          <HomeSectionView
            idx={4}
            label='Dealer Advisor & Sales Coach'
            labelImagePath={
              '/static/home_page/ic_dealer_advisor_and_sales_coach.png'
            }
            title=''
            subtitle=''
            features={[
              {
                title: 'Your Dealership’s Health Check',
                description:
                  ' Dealer Advisor categorizes real-time data into three critical reports—highlighting what is performing well, what requires observation, and which areas demand immediate, urgent attention.',
              },
              {
                title: 'AI Advisor',
                description:
                  ' Move beyond raw analytics by asking the Dealer Advisor any query; it instantly transforms complex sales and engagement metrics into specific recommendations for dealership growth.',
              },
              {
                title: 'Customer Activity Info at Your Fingertips',
                description:
                  ' Dealer Advisor and Sales Coach provide a 360-degree view of every lead, integrating vehicle interests, completed forms, and specific high-intent actions into one actionable report.',
              },
              {
                title: 'Personalized Sales Steps',
                description:
                  'The Sales Coach converts customer behavior into actionable intent scores, giving reps strategic next steps and personalized, ready-to-send text messages for high-impact follow-ups.',
              },
              {
                title: 'On Demand Sales Answers',
                description:
                  'Empower your team to close deals faster by allowing reps to instantly query the AI for deep-dive details on any vehicle or customer history during the sales process.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_dealer_advisor.png'}
            imagePath2={'/static/home_page/img_sales_coach.png'}
            openImageViewView={(_link: string) => {
              openImageViewView(_link);
            }}
          />
        </Box>

        {/* <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            background: `url('/static/home_page/img_purple_blue_banner.png')`,
            backgroundSize: 'cover', // '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: typographyClamps.h3,
              color: 'white',
              fontWeight: 'bold',
            }}
          >
            One Experience. Total Visibility. Better Leads.
          </Typography>

          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              // color: 'gold',
              color: 'black',
              fontWeight: 600,
              mt: 1,
            }}
          >
            From lot scan to real buyer intent — fully connected in real time.
          </Typography>
        </Box> */}

        {/* <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            background: AppColors.aiGradient8,
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <GradientBorderBox gradient={AppColors.aiGradient9}>
            <Typography
              sx={{ fontSize: typographyClamps.h6, color: '#1961ff' }}
            >
              <HtmlRenderer htmlString='<strong>Customer</strong> Experience' />
            </Typography>
          </GradientBorderBox>

          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              fontWeight: 'bold',
            }}
          >
            Designed for How Buyers Actually Shop
          </Typography>

          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              my: 2,
            }}
          >
            A mobile-first AutoSpotlight experience that guides every
            interaction toward clarity, confidence, and next steps.
          </Typography>

          <Box
            component={'img'}
            src='/static/home_page/img_web_mobile.png'
            width={'auto'}
            maxWidth={'100%'}
            mt={{ xs: 1, sm: 2 }}
            mb={{ xs: 2, sm: 4 }}
            sx={{ cursor: 'pointer' }}   // ← visual hint that it's clickable
            onClick={buildClickHandler(ZONES_MOBILE_SCREENS)}
          />

          {getBookADemoButton()}
        </Box> */}

        {/* <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            // background: AppColors.aiGradient8,
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <GradientBorderBox gradient={AppColors.aiGradient9}>
            <Typography
              sx={{ fontSize: typographyClamps.h6, color: '#1961ff' }}
            >
              <HtmlRenderer htmlString='<strong>Dealer</strong> Dashboard' />
            </Typography>
          </GradientBorderBox>

          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              fontWeight: 'bold',
            }}
          >
            Your Command Center for Every Lead
          </Typography>

          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              my: 2,
            }}
          >
            View all customer interactions, manage sales reps, adjust
            incentives, receive real-time notifications, and track speed-to-lead
            performance — from anywhere.
          </Typography>

          {getLearnMoreAndGetStartedButtons()}

          <Box
            component={'img'}
            src='/static/home_page/img_dealer_dashboard.png'
            width={'auto'}
            maxWidth={'100%'}
            mt={{ xs: 1, sm: 2 }}
            sx={{ cursor: 'pointer'}}
            onClick={buildClickHandler(ZONES_COMMAND_CENTER)}
          />
        </Box> */}

        {/* <Grid
          container
          sx={{
            py: 4,
            px: '10%',
            mt: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            background: AppColors.aiGradient8,
          }}
        >
          <Grid
            size={{ xs: 12, sm: 3 }}
            textAlign={{ xs: 'center', sm: 'end' }}
          >
            <Box
              component={'img'}
              src='/static/home_page/img_dealer_dashboard_mobile.png'
              sx={{
                // maxWidth: { xs: '100%', sm: '100%' },
                height: '100%',
                cursor: 'pointer'
              }}
              onClick={buildClickHandler(ZONES_DEALERSHIP_IN_POCKET)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Typography
              sx={{
                fontSize: typographyClamps.h4,
                fontWeight: 'bold',
              }}
            >
              Your Dealership. In your pocket.
            </Typography>
            <Typography
              sx={{
                fontSize: typographyClamps.h5,
                fontWeight: 400,
                mt: { xs: 0, sm: 2 },
              }}
            >
              Stay connected to your sales floor, manager team, and respond to
              leads instantly – wherever business takes you.
            </Typography>
          </Grid>
        </Grid> */}

        {/* <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            // background: AppColors.aiGradient8,
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <GradientBorderBox gradient={AppColors.aiGradient9}>
            <Typography
              sx={{ fontSize: typographyClamps.h6, color: '#1961ff' }}
            >
              <HtmlRenderer htmlString='<strong>Sales Rep</strong> Dashboard' />
            </Typography>
          </GradientBorderBox>

          <Box
            component={'img'}
            src='/static/home_page/img_sales_rep_dashboard.png'
            width={'auto'}
            maxWidth={'100%'}
            mt={{ xs: 1, sm: 2 }}
            mb={{ xs: 2, sm: 4 }}
            sx={{ cursor: 'pointer' }}
            onClick={buildClickHandler(ZONES_STACKED_SCREENS)}
          />

          {getLearnMoreAndGetStartedButtons()}          
        </Box> */}

        {/* <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: `url('/static/benefits_page/img_benefits_tile_bg_big.png')`,
            backgroundSize: '100% auto',
            p: { xs: 1, sm: 2 },
            position: 'relative',
          }}
        >
          <Grid container gap={{ xs: 1, sm: 2 }} justifyContent={'center'}>
            {featureCards.map((item) => {
              return (
                <Grid
                  size={{ xs: 5.7, sm: 5.8, md: 2.8 }}
                  key={item.title}
                  display={'flex'}
                >
                  <Box
                    component={'img'}
                    src={item.iconPath}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      openImageViewView(
                        (e.currentTarget as HTMLImageElement).src,
                      );
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box> */}

        {/* <Box sx={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
          <Box
            component={'img'}
            src={'/static/home_page/img_light_blue_circle_1.png'}
            sx={{
              transform: `rotate(270deg)`,
              position: 'absolute',
              // left: 0,
              left: { xs: '-125%', sm: '-60%', md: '-50%', lg: '-20%' },
              top: 0,
            }}
          />

          <Box
            component={'img'}
            src={'/static/home_page/img_light_blue_circle_1.png'}
            sx={{
              position: 'absolute',
              transform: `rotate(90deg)`,
              // right: 0,
              right: { xs: '-125%', sm: '-70%', md: '-50%', lg: '-20%' },
              bottom: '10%',
            }}
          />

          <Box
            component={'img'}
            src={'/static/images/ic_ai_star.png'}
            sx={{
              position: 'absolute',
              right: '10%',
              top: '10%',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          />

          <Box
            component={'img'}
            src={'/static/images/ic_ai_star.png'}
            sx={{
              position: 'absolute',
              right: '5%',
              top: '50%',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          />

          <Box
            component={'img'}
            src={'/static/images/ic_ai_star.png'}
            sx={{
              position: 'absolute',
              left: '10%',
              bottom: '10%',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          />

          <Box maxWidth='800px' mx='auto' pt={4} px={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                fontSize={typographyClamps.h4}
                align='center'
                // gutterBottom
                color={AppColors.vividBlueColor}
              >
                Frequently Asked Questions
              </Typography>              
            </Box>

            {faqData.map((faq, parentIndex) => (
              <Box>
                <Typography
                  fontSize={typographyClamps.h6}
                  fontWeight={600}
                  // align='center'
                  gutterBottom
                  color={AppColors.textBlackColor}
                  mt={4}
                  // mb={2}
                >
                  {faq.title}
                </Typography>
                {faq.questions.map((item, index) => (
                  <Accordion
                    key={index}
                    expanded={expanded === `panel-${parentIndex}-${index}`}
                    onChange={handleChange(`panel-${parentIndex}-${index}`)}
                    disableGutters
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      // borderTop: index === 0 ? '1px solid #e0e0e0' : 'none',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{ color: AppColors.vividBlueColor }}
                        />
                      }
                    >
                      <Typography
                        sx={{
                          color: AppColors.vividBlueColor,
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      >
                        {item.question}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Typography
                        sx={{
                          color: AppColors.darkGrayColor,
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                        }}
                      >
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))}
          </Box>
        </Box> */}
      </Page>

      {videoUrl && (
        <VideoModel
          onClose={onClose}
          toggle={toggle}
          videoUrl={videoUrl ?? ''}
          title=''
        />
      )}

      {audioUrl && (
        <AudioModel
          onClose={audioOnClose}
          toggle={audioToggle}
          audioUrl={audioUrl ?? ''}
          title=''
        />
      )}

      {/* <GetStartedModel
        onClose={onCloseGetStartedModel}
        toggle={toggleGetStartedModel}
        onSubmitSuccess={() => { }}
      /> */}

      <BookDemoFormModel
        onClose={onCloseRequestDemoModel}
        toggle={toggleRequestDemoModel}
        onSubmitSuccess={() => { }}
      />

      <ImagePreviewDialog
        open={toggleImagePreviewModel}
        onClose={onCloseImagePreviewModel}
        imageSrc={previewImagePath || ''}
        imageAlt='Image'
        title='Image Preview'
      />

      <NfadaPopupModal
        onClose={getStartedOnClose}
        open={getStartedToggle}
        onGetStartedClicked={function (): void {
          handleGetStartedClick();
        }} />

      {togglePricingModel && (
        <PricingModal
          onClose={onClosePricingModel}
          open={togglePricingModel}
          onGetStartedClicked={() => {

            handlePayNow('usub');
          }}
        />
      )}

      <QRBuddySubscriptionDialog
        onClose={onCloseGetStartedModel}
        toggle={toggleGetStartedModel}
        plan={pricingPlans.find((plan) => plan.code === 'usub')!}
      // onGetStartedClicked={handlePayNow}
      />
    </>
  );
};

export default Home;
